/**
 * FlowCard Component
 * 
 * Displays a saved flow in the dashboard grid.
 * Shows thumbnail (last generated asset) and flow name.
 * Supports right-click context menu with Open, Clone, Delete options.
 */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import type { SavedFlow } from '../../types/flow';
import { getImageUrlFromNode } from '../../utils/graphUtils';
import './FlowCard.css';

interface FlowCardProps {
  flow: SavedFlow;
  viewMode: 'grid' | 'list';
  onClick: () => void;
  onClone?: () => void;
  onRename?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  /** Open a project picker for this flow. Optional — when omitted the
   * menu item is hidden so non-Projects builds keep the old menu. */
  onMoveToProject?: () => void;
  /** Selection-mode props. When `selectionMode` is true the card click
   * toggles selection instead of opening the flow, and a checkmark
   * indicator overlays the thumbnail. */
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** Index for staggered animation (0-based) */
  animationIndex?: number;
}

/**
 * Extract the latest image URL from a flow's nodes
 * Checks imagePanel, imagePrompt, customOutput, and custom nodes
 * Falls back to video thumbnail if no images are found
 */
function getFlowThumbnail(flow: SavedFlow): string | null {
  const nodes = flow.state.nodes || [];
  
  // Priority 1: Look for images from image-producing nodes
  for (const node of nodes) {
    const imageUrl = getImageUrlFromNode(node);
    if (imageUrl) {
      return imageUrl;
    }
  }
  
  // Priority 2: Look for videos and use their thumbnails
  for (const node of nodes) {
    const videoUrl = getVideoUrlFromNode(node);
    if (videoUrl) {
      // Generate thumbnail URL from video URL
      // Videos stored at /api/media/{key} have thumbnails at /api/media/{key}/thumbnail
      const thumbnailUrl = getVideoThumbnailUrl(videoUrl);
      if (thumbnailUrl) {
        return thumbnailUrl;
      }
    }
  }
  
  return null;
}

/**
 * Get video URL from a node
 */
function getVideoUrlFromNode(node: any): string | null {
  const nodeData = node.data as any;
  const nodeType = node.type;

  // Custom Output nodes with video
  if (nodeType === 'customOutput') {
    const pages = nodeData.pages || [];
    for (const page of pages) {
      if (page?.type === 'video' && page.videoUrl) {
        return page.videoUrl;
      }
      if (page?.type === 'video' && page.content) {
        return page.content;
      }
    }
    // Legacy: check direct videoUrl
    if (nodeData.type === 'video' && nodeData.videoUrl) {
      return nodeData.videoUrl;
    }
  }

  // Custom nodes with video output
  if (node.type?.startsWith('custom-') && nodeData.outputVideoUrl) {
    return nodeData.outputVideoUrl;
  }

  return null;
}

/**
 * Convert a video URL to its thumbnail URL
 * Videos from our API have thumbnails at {videoUrl}/thumbnail
 */
function getVideoThumbnailUrl(videoUrl: string): string | null {
  if (!videoUrl) return null;
  
  // Only handle our own API media URLs
  if (videoUrl.includes('/api/media/')) {
    // Strip any query params and add /thumbnail
    const baseUrl = videoUrl.split('?')[0];
    return `${baseUrl}/thumbnail`;
  }
  
  return null;
}

export const FlowCard: React.FC<FlowCardProps> = ({
  flow,
  viewMode,
  onClick,
  onClone,
  onRename,
  onArchive,
  onDelete,
  onMoveToProject,
  selectionMode = false,
  selected = false,
  onToggleSelect,
  animationIndex = 0
}) => {
  const { metadata, state } = flow;
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [imageError, setImageError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Node count. The dashboard listing supplies this directly (computed
  // server-side) because those flows are summaries and carry no nodes; a
  // fully-loaded flow falls back to counting them.
  //
  // Undefined means genuinely unknown — a summary from a database that
  // predates the node_count field. Counting the (empty) node array there would
  // render a confident "0 nodes" on every card; the badge is hidden instead.
  const nodeCount = metadata.nodeCount ?? (metadata.isSummary ? undefined : state.nodes?.length ?? 0);

  // Get thumbnail from flow metadata (persisted) or extract from nodes (fallback)
  const thumbnail = useMemo(() => {
    // First check metadata for persisted thumbnail
    if (metadata.thumbnail) {
      return metadata.thumbnail;
    }
    // Otherwise, extract from flow nodes. Only reachable for a fully-loaded
    // flow — dashboard summaries have no nodes to scan, so a legacy flow with
    // no persisted thumbnail shows the placeholder until its next save.
    return getFlowThumbnail(flow);
  }, [flow, metadata]);

  // Calculate animation delay based on index (staggered by 50ms per card)
  const animationStyle = {
    '--animation-delay': `${animationIndex * 0.05}s`
  } as React.CSSProperties;

  // Handle right-click context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  // Close context menu when clicking elsewhere, pressing Escape, or scrolling
  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', closeMenu, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', closeMenu, true);
      };
    }
    return undefined;
  }, [contextMenu]);

  // Context menu actions
  const handleOpen = () => {
    setContextMenu(null);
    onClick();
  };

  const handleClone = () => {
    setContextMenu(null);
    onClone?.();
  };

  const handleRename = () => {
    setContextMenu(null);
    onRename?.();
  };

  const handleArchive = () => {
    setContextMenu(null);
    onArchive?.();
  };

  const handleDelete = () => {
    setContextMenu(null);
    onDelete?.();
  };

  const handleMoveToProject = () => {
    setContextMenu(null);
    onMoveToProject?.();
  };

  // In selection mode the card click toggles selection rather than
  // opening the flow. Context menu is disabled in selection mode to
  // prevent the move/clone/delete actions from running on a single
  // card while the user is curating a multi-select set.
  const handleCardClick = (e: React.MouseEvent) => {
    if (selectionMode) {
      e.preventDefault();
      onToggleSelect?.();
      return;
    }
    onClick();
  };
  const handleCardContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (selectionMode) {
      e.preventDefault();
      onToggleSelect?.();
      return;
    }
    handleContextMenu(e);
  };

  return (
    <>
      <button
        className={`flow-card ${viewMode} flow-card-animate${selected ? ' flow-card-selected' : ''}${selectionMode ? ' flow-card-selecting' : ''}`}
        onClick={handleCardClick}
        onContextMenu={handleCardContextMenu}
        title={metadata.name}
        style={animationStyle}
      >
        {selectionMode && (
          <span
            className={`flow-card-select-indicator${selected ? ' selected' : ''}`}
            aria-hidden
          >
            {selected ? '✓' : ''}
          </span>
        )}
        <div className="flow-card-thumbnail">
        {thumbnail && !imageError ? (
          <img
            src={thumbnail}
            alt={metadata.name}
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flow-card-placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2L2 7L12 12L22 7L12 2Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 17L12 22L22 17"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 12L12 17L22 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
      <div className="flow-card-info">
        <h3 className="flow-card-name">{metadata.name}</h3>
        {viewMode === 'list' && (
          <div className="flow-card-meta">
            {nodeCount !== undefined && (
              <span className="flow-card-nodes">{nodeCount} nodes</span>
            )}
            {metadata.updatedAt && (
              <span className="flow-card-date">{formatDate(metadata.updatedAt)}</span>
            )}
          </div>
        )}
      </div>
    </button>
    
    {/* Context Menu */}
    {contextMenu && (
      <div
        ref={menuRef}
        className="flow-card-context-menu"
        style={{ top: contextMenu.y, left: contextMenu.x }}
      >
        <button className="context-menu-item" onClick={handleOpen}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
          Open
        </button>
        <button className="context-menu-item" onClick={handleClone}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          Clone
        </button>
        <button className="context-menu-item" onClick={handleRename}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          Rename
        </button>
        {onMoveToProject && (
          <button className="context-menu-item" onClick={handleMoveToProject}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Move to project…
          </button>
        )}
        <button className="context-menu-item" onClick={handleArchive}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="21 8 21 21 3 21 3 8" />
            <rect x="1" y="3" width="22" height="5" />
            <line x1="10" y1="12" x2="14" y2="12" />
          </svg>
          {flow.metadata.archived ? 'Unarchive' : 'Archive'}
        </button>
        <div className="context-menu-divider" />
        <button className="context-menu-item context-menu-item-danger" onClick={handleDelete}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
          Delete
        </button>
      </div>
    )}
    </>
  );
};

/**
 * Skeleton loading placeholder for FlowCard
 * Shows a shimmer animation while flows are being loaded
 */
interface SkeletonFlowCardProps {
  viewMode: 'grid' | 'list';
  /** Index for staggered shimmer animation (0-based) */
  index?: number;
}

export const SkeletonFlowCard: React.FC<SkeletonFlowCardProps> = ({ viewMode, index = 0 }) => {
  // Stagger animation delay by 80ms per card for wave effect
  const style = {
    '--skeleton-delay': `${index * 0.08}s`
  } as React.CSSProperties;

  return (
    <div className={`flow-card-skeleton ${viewMode}`} style={style}>
      <div className="skeleton-thumbnail" />
      <div className="skeleton-name" />
    </div>
  );
};

export default FlowCard;
